import {
  AgGridColFactoryService,
  PPSSTabName,
} from '@products/fund-listing/services/ag-grid-col-factory.service';
import {
  CalcTypeComponent,
  CalcTypeGroupHeaderComponent,
  CalcTypeGroupHeaderParams,
} from '@products/fund-listing/ag-grid-helpers/group-headers/calc-type-group-header/calc-type-group-header.component';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ColDef, ColGroupDef } from '@ag-grid-community/core';
import {
  ConfigurationId,
  FundShareClassId,
  PPSSState,
  ProductData,
  ToggleProps,
} from '@types';
import { Subject, Subscription } from 'rxjs';

import { AgGridAngular } from '@ag-grid-community/angular';
import { AgGridHelperService } from '@products/fund-listing/services/ag-grid-helper.service';
import { ExpenseRatioCellRendererComponent } from '@products/fund-listing/ag-grid-helpers/cell-renderers/expense-ratio-cell-renderer/expense-ratio-cell-renderer.component';
import { FundNameCellRendererComponent } from '@products/fund-listing/ag-grid-helpers/cell-renderers/fund-name-cell-renderer/fund-name-cell-renderer.component';
import { PpssCardContainerComponent } from '@products/fund-listing/ppss-card-container/ppss-card-container.component';
import { PpssService } from '@products/fund-listing/services/ppss.service';
import { PpssTableBase } from '@products/fund-listing/ppss-table-base';
import { TranslateService } from '@shared/translate/translate.service';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { PpssServiceFactory } from '@products/fund-listing/services/ppss.service.factory';
import { FavFundCellRendererComponent } from '@products/fund-listing/ag-grid-helpers/cell-renderers/fav-fund-cell-renderer/fav-fund-cell-renderer.component';

@Component({
  selector: 'ft-ppss-yields',
  templateUrl: './ppss-yields.component.html',
  styleUrls: ['./ppss-yields.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PpssYieldsComponent
  extends PpssTableBase
  implements OnInit, OnChanges, OnDestroy, CalcTypeComponent {
  @Input() isHandheld = false;
  @Input() active;
  navAsOfDate: string;
  stdYieldThirtyDayAsofDate: string;
  calcType: ToggleProps | string;
  productType: ConfigurationId;
  currentPage: ProductData[];
  calcTypeParams: CalcTypeGroupHeaderParams;
  private ppssSubscription: Subscription;
  private unsubscribe$: Subject<void> = new Subject<void>();
  private ppssService: PpssService;
  // AG-Grid specific variables.
  columnDefs: ColGroupDef[] = [];
  defaultColDef: ColDef = {
    sortable: true,
    cellClass: ['ag-cell-frk--highlightedTertiary'],
    flex: 1,
  };
  gridContext = {
    filteredRows: [],
  };
  frameworkComponents = {
    calcTypeGroupHeaderComponent: CalcTypeGroupHeaderComponent,
    fundNameCellRendererComponent: FundNameCellRendererComponent,
    expenseRatioCellRendererComponent: ExpenseRatioCellRendererComponent,
    favFundCellRendererComponent: FavFundCellRendererComponent,
  };
  isSiteIntl: boolean;

  @ViewChild('yieldGrid', { static: false }) yieldGrid: AgGridAngular;
  @ViewChild('yieldCards', { static: false })
  yieldCards: PpssCardContainerComponent;
  numberofFilteredData: number;

  constructor(
    private cdr: ChangeDetectorRef,
    ppssServiceFactory: PpssServiceFactory,
    private translateService: TranslateService,
    private agGridHelperService: AgGridHelperService,
    private agGridColFactoryService: AgGridColFactoryService
  ) {
    super();
    this.ppssService = ppssServiceFactory.getPpssService();
    this.productType = this.ppssService.getProductType();
    this.calcType = this.agGridHelperService.calcTypeConfiguration[
      this.productType
    ];
  }

  ngOnInit() {
    // for now, don't detach() as it stops ExpandCollapse component working in Mobile view
    // TODO: Figure out a solution. May require ExpandCollapse component to use explicit change detection
    // this.cdr.detach();
    if (this.ppssService) {
      this.ppssSubscription = this.ppssService.subscribe((state) =>
        this.mapState(state)
      );
      this.handlePagination();
    }
  }

  private handlePagination() {
    this.ppssService.getNumberOfPages$().subscribe(() => {
      this.yieldGrid?.api?.paginationSetPageSize(
        Number(this.ppssService.getPageSize())
      );
      this.cdr.detectChanges();
    });
    this.ppssService
      .getCurrentPage$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((pageData: ProductData[]): void => {
        this.currentPage = pageData;
        this.cdr.detectChanges();
      });
    this.ppssService
      .getPageNumber$()
      .pipe(takeUntil(this.unsubscribe$), distinctUntilChanged())
      .subscribe((n: number): void => {
        this.yieldGrid?.api.paginationGoToPage(n - 1);

        if (!this.ppssService._isFilterProcessing) {
          const firstRowIndex =
            this.yieldGrid?.api.paginationGetPageSize() * (n - 1);
          this.yieldGrid?.api.setFocusedCell(firstRowIndex, 'FUND-NAME');
        }
        this.redrawGrid(this);
      });
  }
  redrawGrid(ref) {
    ref.yieldGrid?.api.redrawRows();
  }
  ngOnDestroy() {
    this.ppssSubscription?.unsubscribe(); // TODO could switch to use async pipe
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnChanges(changes: SimpleChanges) {
    // need to explicitly run detectChanges() when isHandheld changes, because component has been detached from change detection earlier
    if (changes.isHandheld) {
      this.cdr.detectChanges();
    }
    if (this.active) {
      const that = this;
      setTimeout(() => {
        that.redrawGrid(that);
      }, 1000);
    }
  }

  /**
   * Called when the state changes.
   * @param state initial/refreshed state.
   */
  mapState(state: PPSSState) {
    if (state.lastAction === 'populate' || this.firstTime) {
      this.onPopulate(state);
    }
    if (state.lastAction === 'changeCalcTab' || this.firstTime) {
      this.setSelectedCalcType(state);
    }
    if (state.lastAction === 'filterRows' || this.firstTime) {
      this.numberofFilteredData = state.data.filteredTableRows.length;
      this.cdr.detectChanges();
      this.gridContext.filteredRows = state.data.filteredTableRows;
      this.yieldGrid?.api.onFilterChanged();
      this.yieldGrid?.api.resetRowHeights();
    }

    if (state.lastAction === 'updateRows') {
      this.yieldGrid?.api.resetRowHeights();
    }

    this.firstTime = false;
  }

  isExternalFilterPresent = () => true;

  doesExternalFilterPass(node: any) {
    if (node.gridOptionsWrapper.getContext().filteredRows) {
      return (
        node.gridOptionsWrapper.getContext().filteredRows.indexOf(node.data) >
        -1
      );
    }
    return true;
  }

  /**
   * Called on initial population of the data and config.
   * Set config details and define column defs.
   * @param state initial state.
   */
  onPopulate(state: PPSSState) {
    super.onPopulate(state);

    if (!this.isPopulated) {
      return;
    }

    this.navAsOfDate = state.data.asOfDates.navAsOfDate;
    this.stdYieldThirtyDayAsofDate =
      state.data.asOfDates.stdYieldThirtyDayAsofDate;

    // NGC-10993: Restrict the calcType assignment in case of US-MF as it would be 'NET' always.
    if (state.isSiteIntl || this.productType !== ConfigurationId.MF) {
      this.initialCalc = state.config.calcType ? state.config.calcType : 'OFF';

      if (state.config.calcType) {
        if (typeof this.calcType === 'string') {
          this.calcType = state.config.calcType;
        } else {
          (this.calcType as ToggleProps).selected = state.config.calcType;
        }
      }
    }

    this.columnDefs = this.agGridColFactoryService.generateColDefs(
      PPSSTabName.YIELDS,
      this,
      state,
      this.agGridHelperService,
      this.translateService
    ) as ColGroupDef[];

    this.cdr.detectChanges();
  }

  /**
   * Set the calcType as per the toggle done.
   * @param state refreshed state.
   */
  private setSelectedCalcType(state: PPSSState) {
    // NGC-10993: Don't trigger the 'calcType' change from performance switch button.
    // It would always be 'NET' for 'US'-'MF'.
    if (state.isSiteIntl || this.productType !== ConfigurationId.MF) {
      if (typeof this.calcType === 'string') {
        this.calcType = state.config.calcType;
      } else {
        (this.calcType as ToggleProps).selected = state.config.calcType;
      }
      this.yieldGrid?.gridOptions?.api?.refreshCells({
        columns: this.yieldGrid.columnApi
          .getColumnGroup('DISTRIBUTION')
          ?.getLeafColumns(),
      });
      this.yieldGrid?.gridOptions?.api?.refreshHeader();
      this.yieldCards?.refresh();
      this.cdr.detectChanges();
    }
  }

  /**
   * Update the nav change value to the state.
   * @param tab the switch value. Ex. nav/pop
   */
  updateCalcType(tab: string) {
    this.ppssService.changeCalcTab(tab);
  }

  updateFavourite(fund: FundShareClassId) {
    this.ppssService.updateFavourite(fund);
  }
}
